/* @jsx h */
import { h } from "vue";
import type { Reservation, ReservationRequest } from "@lemmonhq/types";
import { formatDateInTimezone } from "@lemmonhq/orders";
import { ReservationRequestCard } from "../ReservationRequestCard";
import { ReservationCard } from "../ReservationCard";
import { DateGroupHeader } from "./DateGroupHeader";

interface ContentListProps {
  requests: ReservationRequest[];
  reservations: Reservation[];
  isRequestView: boolean;
  loadingMore: boolean;
  onRequestClick: (request: ReservationRequest) => void;
  onRequestAccept: (request: ReservationRequest) => void;
  onRequestReject: (request: ReservationRequest) => void;
  onReservationClick: (reservation: Reservation) => void;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  viewMode?: "day" | "week" | "all";
  restaurantTimeZone: string;
}

export const ContentList = (props: ContentListProps) => {
  return (
    <div class="grid gap-2">
      {props.isRequestView ? (
        <>
          {(() => {
            // Group requests by restaurant-local date so headers and buckets stay aligned.
            const grouped: {
              key: string;
              label: string;
              items: typeof props.requests;
            }[] = [];
            let lastKey = "";
            for (const request of props.requests) {
              const key = formatDateInTimezone(
                request.requested_time,
                props.restaurantTimeZone,
                {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                },
              );
              if (key !== lastKey) {
                const label = formatDateInTimezone(
                  request.requested_time,
                  props.restaurantTimeZone,
                  {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  },
                );
                grouped.push({ key, label, items: [] });
                lastKey = key;
              }
              grouped[grouped.length - 1].items.push(request);
            }
            return grouped.map(({ key, label, items: requests }) => (
              <div key={key}>
                <DateGroupHeader date={label} count={requests.length} />
                <div class="grid gap-2">
                  {requests.map((request) => (
                    <ReservationRequestCard
                      key={request.id}
                      request={request}
                      linkedReservation={
                        request.reservation_id
                          ? props.reservations.find(
                              (res) => res.id === request.reservation_id,
                            ) || null
                          : null
                      }
                      onClick={(req) => {
                        if (req.status === "pending") {
                          props.onRequestAccept(req);
                        } else {
                          props.onRequestClick(req);
                        }
                      }}
                      onAccept={props.onRequestAccept}
                      onReject={props.onRequestReject}
                      selected={props.selectedIds?.has(request.id)}
                      onToggleSelect={props.onToggleSelect}
                    />
                  ))}
                </div>
              </div>
            ));
          })()}
        </>
      ) : (
        <>
          {props.viewMode === "week"
            ? // Week view: group by restaurant-local date
              (() => {
                const groups: {
                  key: string;
                  label: string;
                  items: typeof props.reservations;
                }[] = [];
                let lastKey = "";
                for (const res of props.reservations) {
                  const key = formatDateInTimezone(
                    res.time,
                    props.restaurantTimeZone,
                    {
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                    },
                  );
                  if (key !== lastKey) {
                    const label = formatDateInTimezone(
                      res.time,
                      props.restaurantTimeZone,
                      {
                        weekday: "long",
                        month: "short",
                        day: "numeric",
                      },
                    );
                    groups.push({ key, label, items: [] });
                    lastKey = key;
                  }
                  groups[groups.length - 1].items.push(res);
                }
                return groups.map(({ key, label, items }) => (
                  <div key={key}>
                    <DateGroupHeader date={label} count={items.length} />
                    <div class="grid gap-2">
                      {items.map((reservation) => (
                        <ReservationCard
                          key={reservation.id}
                          reservation={reservation}
                          onClick={props.onReservationClick}
                        />
                      ))}
                    </div>
                  </div>
                ));
              })()
            : // Day view: flat list
              props.reservations.map((reservation) => (
                <ReservationCard
                  key={reservation.id}
                  reservation={reservation}
                  onClick={props.onReservationClick}
                />
              ))}
        </>
      )}

      {props.loadingMore && (
        <div class="flex justify-center py-4">
          <span class="loading loading-spinner loading-md text-primary"></span>
        </div>
      )}
    </div>
  );
};
